import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Copy, RefreshCw } from 'lucide-react';

const PasswordGeneratorPage = () => {
  const [length, setLength] = useState('16');
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const generatePassword = () => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') {
      alert('Please select at least one character type');
      return;
    }

    let result = '';
    const len = parseInt(length);
    for (let i = 0; i < len; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(result);
    setCopied(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStrength = () => {
    if (!password) return { text: '', color: '' };
    const len = password.length;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const variety = [hasUpper, hasLower, hasNumber, hasSymbol].filter(Boolean).length;

    if (len >= 16 && variety >= 3) return { text: 'Very Strong', color: 'text-green-400' };
    if (len >= 12 && variety >= 3) return { text: 'Strong', color: 'text-cyan-400' };
    if (len >= 8 && variety >= 2) return { text: 'Medium', color: 'text-yellow-400' };
    return { text: 'Weak', color: 'text-red-400' };
  };

  const strength = getStrength();

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Password Generator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-2">Password Generator</h1>
          <p className="text-gray-400 mb-8">Generate secure random passwords.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            {password && (
              <div className="mb-6 p-4 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
                <div className="flex items-center justify-between gap-4">
                  <code className="text-cyan-400 text-lg font-mono break-all flex-1">{password}</code>
                  <button onClick={copyToClipboard} className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors">
                    <Copy className={`w-5 h-5 ${copied ? 'text-green-400' : 'text-gray-400'}`} />
                  </button>
                </div>
                {strength.text && <p className={`text-sm mt-2 ${strength.color}`}>Strength: {strength.text}</p>}
              </div>
            )}
            <div className="space-y-5">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Password Length: {length}</label>
                <input type="range" min="4" max="64" value={length} onChange={(e) => setLength(e.target.value)} className="w-full accent-cyan-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={includeUppercase} onChange={(e) => setIncludeUppercase(e.target.checked)} className="w-4 h-4 accent-cyan-500" /><span className="text-gray-300">Uppercase (A-Z)</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={includeLowercase} onChange={(e) => setIncludeLowercase(e.target.checked)} className="w-4 h-4 accent-cyan-500" /><span className="text-gray-300">Lowercase (a-z)</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={includeNumbers} onChange={(e) => setIncludeNumbers(e.target.checked)} className="w-4 h-4 accent-cyan-500" /><span className="text-gray-300">Numbers (0-9)</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={includeSymbols} onChange={(e) => setIncludeSymbols(e.target.checked)} className="w-4 h-4 accent-cyan-500" /><span className="text-gray-300">Symbols (!@#$...)</span></label>
              </div>
            </div>
            <Button onClick={generatePassword} className="mt-5 bg-cyan-600 hover:bg-cyan-500 text-white flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Generate Password
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PasswordGeneratorPage;
